import React, { useState, useRef, useMemo, useEffect } from 'react';
import { useLoadScript, GoogleMap, Marker } from '@react-google-maps/api';
import '../styles.css';
import HeaderTotal from './header_total';

const libraries = ['places'];

const FormularioDireccion = ({ onContinuar }) => {
  const [center, setCenter] = useState({ lat: 19.4326, lng: -99.1332 });
  const [marker, setMarker] = useState(null);
  const [canEdit, setCanEdit] = useState(false); // NUEVO: habilita/inhabilita campos inferiores

  const [address, setAddress] = useState({
    calle: '',
    colonia: '',
    municipio: '',
    cp: '',
    numExt: '',
    numInt: '',
    referencias: '',
    latitud: '',
    longitud: ''
  });

  const [errors, setErrors] = useState({});
  const addressInputRef = useRef(null);
  const autocompleteInstance = useRef(null);

  const { isLoaded } = useLoadScript({
    googleMapsApiKey: 'AIzaSyB9MUmEysYovfNTHDxVZoQo8cEVoVvDsmw',
    libraries,
  });

  useEffect(() => {
    // Inicializa Autocomplete cuando cargue el script
    if (isLoaded && addressInputRef.current) {
      autocompleteInstance.current = new window.google.maps.places.Autocomplete(
        addressInputRef.current
      );

      // Si el usuario selecciona una sugerencia
      autocompleteInstance.current.addListener('place_changed', () => {
        const place = autocompleteInstance.current.getPlace();

        let lat = 0;
        let lng = 0;

        if (place.geometry && place.geometry.location) {
          const newCenter = {
            lat: place.geometry.location.lat(),
            lng: place.geometry.location.lng(),
          };
          setCenter(newCenter);
          setMarker(newCenter);

          lat = newCenter.lat;
          lng = newCenter.lng;

          const component = place.address_components || [];
          let calle = '';
          let colonia = '';
          let municipio = '';
          let cp = '';
          let numExt = '';

          for (const item of component) {
            if (item.types.includes('route')) calle = item.long_name;
            if (item.types.includes('sublocality_level_1')) colonia = item.long_name;
            if (item.types.includes('locality')) municipio = item.long_name;
            if (item.types.includes('street_number')) numExt = item.long_name;
            if (item.types.includes('postal_code')) cp = item.long_name;
          }

          setAddress({
            calle: `${calle}`,
            colonia,
            municipio,
            cp,
            numExt,
            numInt: '',
            referencias: '',
            latitud: lat,
            longitud: lng
          });

          // Borra errores de campos rellenados
          setErrors(prev => {
            const n = { ...prev };
            if (calle) delete n.calle;
            if (colonia) delete n.colonia;
            if (numExt) delete n.numExt;
            if (municipio) delete n.municipio;
            if (cp) delete n.cp;
            return n;
          });

          // Habilita edición
          setCanEdit(true); // NUEVO
        }
      });
    }
  }, [isLoaded]);

  const geocodeLatLng = (latlng, callback) => {
    const geocoder = new window.google.maps.Geocoder();
    geocoder.geocode({ location: latlng }, (results, status) => {
      if (status === 'OK') {
        callback(results?.[0] || null);
      } else {
        console.error('Geocoder failed due to: ' + status);
        callback(null);
      }
    });
  };

  const handleFindMe = () => {
    if (navigator.geolocation) {
      navigator.geolocation.getCurrentPosition(
        (position) => {
          const userLocation = {
            lat: position.coords.latitude,
            lng: position.coords.longitude,
          };
          setCenter(userLocation);
          setMarker(userLocation);
          setAddress(prev => ({
            ...prev,
            latitud: userLocation.lat,
            longitud: userLocation.lng,
          }));

          geocodeLatLng(userLocation, (place) => {
            if (place) {
              const component = place.address_components || [];
              let calle = '';
              let colonia = '';
              let municipio = '';
              let cp = '';
              let numExt = '';

              for (const item of component) {
                if (item.types.includes('route')) calle = item.long_name;
                if (item.types.includes('sublocality_level_1')) colonia = item.long_name;
                if (item.types.includes('locality')) municipio = item.long_name;
                if (item.types.includes('street_number')) numExt = item.long_name;
                if (item.types.includes('postal_code')) cp = item.long_name;
              }

              setAddress(prev => ({
                ...prev,
                calle: `${calle}`,
                colonia, municipio, cp, numExt,
                numInt: '',
                referencias: prev.referencias || ''
              }));
            }
            setCanEdit(true); // NUEVO: también habilita con “Encuéntrame”
          });
        },
        () => alert('No se pudo obtener tu ubicación.')
      );
    } else {
      alert('La geolocalización no es compatible con tu navegador.');
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setAddress({ ...address, [name]: value });
    setErrors({ ...errors, [name]: '' });
  };

  // NUEVO: detectar escritura en el input de dirección superior
  const handleDireccionTyping = (e) => {
    const hasText = e.target.value.trim().length > 0;
    setCanEdit(hasText);
  };

  const validateForm = () => {
    const newErrors = {};
    if (!address.calle.trim()) newErrors.calle = 'La calle es obligatoria.';
    if (!address.numExt.trim()) newErrors.numExt = 'El número exterior es obligatorio.';
    if (!address.cp.trim()) newErrors.cp = 'El código postal es obligatorio';
    if (!address.colonia.trim()) newErrors.colonia = 'La colonia es obligatoria.';
    if (!address.municipio.trim()) newErrors.municipio = 'El municipio/delegación es obligatorio';

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (validateForm()) {
      onContinuar({ direccion: address });
    }
  };

  const onMarkerDragEnd = (event) => {
    const newLat = event.latLng.lat();
    const newLng = event.latLng.lng();
    const newPosition = { lat: newLat, lng: newLng };

    setMarker(newPosition);
    setCenter(newPosition);

    setAddress(prev => ({
      ...prev,
      latitud: newLat,
      longitud: newLng
    }));
  };

  const mapOptions = useMemo(() => ({ disableDefaultUI: true, zoomControl: true }), []);
  const mapContainerStyle = { width: '100%', height: '300px', borderRadius: '8px', margin: '20px 0' };

  if (!isLoaded) return <div>Cargando Google Maps...</div>;

  const hasAnyAddressPiece =
    [address.calle, address.numExt, address.colonia, address.municipio, address.cp].filter(Boolean).length > 0;

  return (
    <form onSubmit={handleSubmit}>
      <div className="contenedor-formulario">
        <HeaderTotal total="" />
        <div className="formulario-rotoplas">
          <h2 className="titulo-seccion">Dirección de entrega</h2>

          <div className="grupo-input">
            <input
              type="text"
              id="direccion"
              name="direccion"
              placeholder="Escriba aquí su dirección"
              ref={addressInputRef}
              onChange={handleDireccionTyping} // NUEVO: habilita campos al escribir
            />
            {!canEdit && (
              <p className="ayuda">
                Escribe tu dirección arriba o usa <strong>Encuéntrame</strong> para habilitar los campos.
              </p>
            )}
          </div>

          <div className="map-container">
            <GoogleMap
              mapContainerStyle={mapContainerStyle}
              center={center}
              zoom={15}
              options={mapOptions}
            >
              {marker && (
                <Marker
                  position={marker}
                  draggable={true}
                  onDragEnd={onMarkerDragEnd}
                />
              )}
            </GoogleMap>
            <button type="button" onClick={handleFindMe} className="boton-encuentrame">
              <span className="icon-encontrar">📍</span> Encuéntrame
            </button>
          </div>

          {hasAnyAddressPiece ? (
            <p className="direccion-display">
              {`${address.calle || ''} ${address.numExt || ''}, ${address.colonia || ''}, ${address.municipio || ''}, ${address.cp || ''}`}
            </p>
          ) : (<p></p>)}

          <div className="grupo-input">
            <input
              type="text"
              placeholder="Calle*"
              name="calle"
              value={address.calle}
              onChange={handleChange}
              disabled={!canEdit} // NUEVO
            />
            {errors.calle && <p className="mensaje-error">{errors.calle}</p>}
          </div>

          <div className="grupo-input-row">
            <input
              type="text"
              placeholder="Número Exterior*"
              name="numExt"
              value={address.numExt}
              onChange={handleChange}
              className="input-half"
              disabled={!canEdit} // NUEVO
            />
            <input
              type="text"
              placeholder="Núm. Int"
              name="numInt"
              value={address.numInt}
              onChange={handleChange}
              className="input-half"
              disabled={!canEdit} // NUEVO
            />
          </div>
          {errors.numExt && <p className="mensaje-error">{errors.numExt}</p>}

          <div className="grupo-input">
            <input
              type="tel"
              placeholder="Código Postal*"
              name="cp"
              value={address.cp}
              onChange={handleChange}
              disabled={!canEdit} // NUEVO
            />
            {errors.cp && <p className="mensaje-error">{errors.cp}</p>}
          </div>

          <div className="grupo-input">
            <input
              type="text"
              placeholder="Colonia*"
              name="colonia"
              value={address.colonia}
              onChange={handleChange}
              disabled={!canEdit} // NUEVO
            />
            {errors.colonia && <p className="mensaje-error">{errors.colonia}</p>}
          </div>

          <div className="grupo-input">
            <input
              type="text"
              placeholder="Municipio/Delegación*"
              name="municipio"
              value={address.municipio}
              onChange={handleChange}
              disabled={!canEdit} // NUEVO
            />
            {errors.municipio && <p className="mensaje-error">{errors.municipio}</p>}
          </div>

          <div className="grupo-input">
            <textarea
              placeholder="Referencias: color de la fachada, entre qué calles está."
              name="referencias"
              value={address.referencias}
              onChange={handleChange}
              disabled={!canEdit} // NUEVO
            />
          </div>

          <button className="boton-next">Continuar</button>
        </div>
      </div>
    </form>
  );
};

export default FormularioDireccion;
